import { useState, type Dispatch, type SetStateAction } from "react";
import type { TFunction } from "i18next";
import { stripHtmlFromPlainText } from "@tabbio/utils";
import type { CVExperience } from "../../../lib/cv-mock-data";
import {
  normalizeExperiencePeriod,
  validateExperience,
} from "./experience-validation";

function newDraftId(): string {
  return `local-${Math.random().toString(36).slice(2, 11)}`;
}

export function emptyExperienceItem(): CVExperience {
  return {
    id: newDraftId(),
    role: "",
    company: "",
    location: "",
    locationType: "",
    employmentType: "",
    period: "",
    overview: "",
    bullets: [],
  };
}

interface UseExperienceReviewQueueParams {
  t: TFunction;
  setItems: Dispatch<SetStateAction<CVExperience[]>>;
  labels: { addTitle: string; editTitle: string };
  onModalReset?: () => void;
}

/**
 * Shared native/web review queue for AI-drafted positions. A single prompt may
 * describe promotions or multiple roles, so each draft is reviewed and
 * validated separately before it is added to the user's experience list.
 */
export function useExperienceReviewQueue({
  t,
  setItems,
  labels,
  onModalReset,
}: UseExperienceReviewQueueParams) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<CVExperience>(emptyExperienceItem());
  const [showErrors, setShowErrors] = useState(false);
  const [reviewQueue, setReviewQueue] = useState<CVExperience[]>([]);
  const [reviewTotal, setReviewTotal] = useState(0);

  const errors = validateExperience(draft, t);
  const hasErrors = Object.keys(errors).length > 0;

  const resetQueue = () => {
    setReviewQueue([]);
    setReviewTotal(0);
  };

  const showGapsFor = (entry: CVExperience) =>
    setShowErrors(Object.keys(validateExperience(entry, t)).length > 0);

  const beginReview = (drafts: CVExperience[]) => {
    const [first, ...rest] = drafts;
    if (!first) return;
    setReviewQueue(rest);
    setReviewTotal(drafts.length);
    setDraft(first);
    setEditingIndex(-1);
    showGapsFor(first);
    onModalReset?.();
  };

  const openAdd = () => {
    setDraft(emptyExperienceItem());
    setEditingIndex(-1);
    setShowErrors(false);
    resetQueue();
    onModalReset?.();
  };

  const openEdit = (item: CVExperience, index: number) => {
    setDraft({ ...item, bullets: [...item.bullets] });
    setEditingIndex(index);
    setShowErrors(false);
    resetQueue();
    onModalReset?.();
  };

  const close = () => {
    setEditingIndex(null);
    setShowErrors(false);
    resetQueue();
    onModalReset?.();
  };

  const commit = () => {
    if (hasErrors) {
      setShowErrors(true);
      return;
    }
    const normalizedDraft = {
      ...draft,
      // Store the picker's own period shape, so an entry that arrived from an
      // import with "2019 to 2021" is written back as "2019 - 2021".
      period: normalizeExperiencePeriod(draft.period),
      overview: stripHtmlFromPlainText(draft.overview),
      bullets: draft.bullets
        .map(stripHtmlFromPlainText)
        .map((bullet) => bullet.trim())
        .filter(Boolean),
    };
    if (editingIndex === -1) {
      // New positions belong at the top of the CV. When one AI request
      // produces several roles, insert each reviewed role after the preceding
      // role from that same batch so their generated order stays intact.
      const insertionIndex = Math.max(reviewPosition - 1, 0);
      setItems((previous) => {
        const next = [...previous];
        next.splice(insertionIndex, 0, normalizedDraft);
        return next;
      });
    } else if (editingIndex !== null) {
      setItems((previous) => {
        const next = [...previous];
        next[editingIndex] = normalizedDraft;
        return next;
      });
    }

    if (reviewQueue.length > 0) {
      const [next, ...rest] = reviewQueue;
      setReviewQueue(rest);
      setDraft(next);
      setEditingIndex(-1);
      showGapsFor(next);
      onModalReset?.();
      return;
    }

    setEditingIndex(null);
    setShowErrors(false);
    setReviewTotal(0);
  };

  const reviewPosition = reviewTotal > 1 ? reviewTotal - reviewQueue.length : 0;
  const baseTitle = editingIndex === -1 ? labels.addTitle : labels.editTitle;
  const modalTitle =
    reviewPosition > 0
      ? `${baseTitle} (${reviewPosition}/${reviewTotal})`
      : baseTitle;

  return {
    editingIndex,
    draft,
    setDraft,
    showErrors,
    setShowErrors,
    errors,
    hasErrors,
    modalTitle,
    beginReview,
    openAdd,
    openEdit,
    close,
    commit,
  };
}
